import { useCallback, useEffect, useState, type FormEvent, type ReactNode } from "react";
import { Loader2, LockKeyhole, LogOut, ShieldCheck, ShieldX } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useNoIndex } from "@/hooks/useNoIndex";

type GateState =
  | "checking"
  | "signed_out"
  | "denied"
  | "mfa_enroll"
  | "mfa_challenge"
  | "authorized"
  | "unavailable";

type TotpEnrollment = {
  factorId: string;
  qrCode: string;
  secret: string;
};

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string;
const PUBLISHABLE_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY as string;

const InternalAccessGate = ({ children }: { children: ReactNode }) => {
  useNoIndex();
  const { user, session, loading, signOut } = useAuth();
  const [state, setState] = useState<GateState>("checking");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [factorId, setFactorId] = useState<string | null>(null);
  const [enrollment, setEnrollment] = useState<TotpEnrollment | null>(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const prepareMfa = useCallback(async () => {
    const { data, error } = await supabase.auth.mfa.listFactors();
    if (error) throw error;
    const verified = data.totp.find((factor) => factor.status === "verified");
    if (verified) {
      setFactorId(verified.id);
      setEnrollment(null);
      setState("mfa_challenge");
      return;
    }

    const { data: enrolled, error: enrollError } = await supabase.auth.mfa.enroll({
      factorType: "totp",
      friendlyName: "Dibs internal",
    });
    if (enrollError) throw enrollError;
    setFactorId(enrolled.id);
    setEnrollment({
      factorId: enrolled.id,
      qrCode: enrolled.totp.qr_code,
      secret: enrolled.totp.secret,
    });
    setState("mfa_enroll");
  }, []);

  const checkOperatorSession = useCallback(async (accessToken?: string) => {
    const token = accessToken ?? session?.access_token;
    if (!token) {
      setState("signed_out");
      return;
    }
    setState("checking");
    setMessage(null);
    try {
      const response = await fetch(
        `${SUPABASE_URL}/functions/v1/internal-operator-session`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            apikey: PUBLISHABLE_KEY,
            "Content-Type": "application/json",
          },
          body: "{}",
        },
      );
      const body = await response.json().catch(() => ({}));
      if (response.status === 401) {
        await signOut();
        setState("signed_out");
        return;
      }
      if (response.status === 403 || body?.error === "operator_access_denied") {
        setState("denied");
        return;
      }
      if (!response.ok || body?.authorized !== true) {
        setState("unavailable");
        return;
      }
      if (body.aal !== "aal2") {
        await prepareMfa();
        return;
      }
      setState("authorized");
    } catch {
      setState("unavailable");
    }
  }, [prepareMfa, session?.access_token, signOut]);

  useEffect(() => {
    if (loading) return;
    if (!user || !session) {
      setState("signed_out");
      return;
    }
    void checkOperatorSession();
  }, [loading, user, session, checkOperatorSession]);

  const handleSignIn = async (event: FormEvent) => {
    event.preventDefault();
    if (!email.trim() || !password) return;
    setBusy(true);
    setMessage(null);
    const { error } = await supabase.auth.signInWithPassword({
      email: email.trim().toLowerCase(),
      password,
    });
    if (error) setMessage("Sign-in failed. Check your credentials and try again.");
    setBusy(false);
  };

  const handleMfaVerify = async (event: FormEvent) => {
    event.preventDefault();
    if (!factorId || !/^\d{6}$/.test(code.trim())) {
      setMessage("Enter the six-digit code from your authenticator app.");
      return;
    }
    setBusy(true);
    setMessage(null);
    const { error } = await supabase.auth.mfa.challengeAndVerify({
      factorId,
      code: code.trim(),
    });
    if (error) {
      setMessage("That verification code was not accepted.");
      setBusy(false);
      return;
    }
    const { data: refreshed, error: refreshError } = await supabase.auth.refreshSession();
    const accessToken = refreshed.session?.access_token;
    if (refreshError || !accessToken) {
      setMessage("Your secure session could not be refreshed. Sign in and try again.");
      setBusy(false);
      return;
    }
    setCode("");
    setBusy(false);
    await checkOperatorSession(accessToken);
  };

  if (loading || state === "checking") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (state === "authorized") return <>{children}</>;

  if (state === "signed_out") {
    return (
      <InternalCard icon={<LockKeyhole className="h-7 w-7 text-primary" />} title="Dibs internal sign in" description="Use your named Dibs employee account. Shared unlock links are no longer accepted.">
        <form className="space-y-4" onSubmit={handleSignIn}>
          <div className="space-y-2">
            <Label htmlFor="internal-email">Email</Label>
            <Input id="internal-email" type="email" autoComplete="username" value={email} onChange={(event) => setEmail(event.target.value)} required />
          </div>
          <div className="space-y-2">
            <Label htmlFor="internal-password">Password</Label>
            <Input id="internal-password" type="password" autoComplete="current-password" value={password} onChange={(event) => setPassword(event.target.value)} required />
          </div>
          {message && <p className="text-sm text-destructive">{message}</p>}
          <Button className="w-full" type="submit" disabled={busy}>
            {busy && <Loader2 className="h-4 w-4 animate-spin" />}
            Sign in
          </Button>
        </form>
      </InternalCard>
    );
  }

  if (state === "mfa_enroll" || state === "mfa_challenge") {
    return (
      <InternalCard icon={<ShieldCheck className="h-7 w-7 text-primary" />} title={state === "mfa_enroll" ? "Set up multi-factor authentication" : "Verify multi-factor authentication"} description={state === "mfa_enroll" ? "Scan this QR code with your authenticator app, then enter the six-digit code." : "Enter the six-digit code from your authenticator app to continue."}>
        {enrollment && (
          <div className="space-y-3 rounded-lg border bg-white p-4 text-center">
            <img src={enrollment.qrCode} alt="Authenticator enrollment QR code" className="mx-auto h-48 w-48" />
            <details className="text-left text-xs text-muted-foreground">
              <summary className="cursor-pointer">Can&apos;t scan the code?</summary>
              <code className="mt-2 block break-all rounded bg-muted p-2">{enrollment.secret}</code>
            </details>
          </div>
        )}
        <form className="space-y-4" onSubmit={handleMfaVerify}>
          <div className="space-y-2">
            <Label htmlFor="internal-mfa">Verification code</Label>
            <Input id="internal-mfa" inputMode="numeric" autoComplete="one-time-code" pattern="[0-9]{6}" maxLength={6} value={code} onChange={(event) => setCode(event.target.value.replace(/\D/g, "").slice(0, 6))} required />
          </div>
          {message && <p className="text-sm text-destructive">{message}</p>}
          <Button className="w-full" type="submit" disabled={busy}>
            {busy && <Loader2 className="h-4 w-4 animate-spin" />}
            Verify and continue
          </Button>
        </form>
      </InternalCard>
    );
  }

  return (
    <InternalCard icon={<ShieldX className="h-7 w-7 text-destructive" />} title={state === "denied" ? "Internal access denied" : "Internal access unavailable"} description={state === "denied" ? "This signed-in account is not an active Dibs internal operator." : "The internal authorization service could not be verified. Access remains closed."}>
      <div className="flex gap-3">
        {state === "unavailable" && <Button variant="outline" className="flex-1" onClick={() => void checkOperatorSession()}>Retry</Button>}
        <Button variant="outline" className="flex-1" onClick={() => void signOut()}><LogOut className="h-4 w-4" /> Sign out</Button>
      </div>
    </InternalCard>
  );
};

const InternalCard = ({ icon, title, description, children }: { icon: ReactNode; title: string; description: string; children: ReactNode }) => (
  <div className="min-h-screen bg-background flex items-center justify-center p-6">
    <Card className="w-full max-w-md">
      <CardHeader className="text-center">
        <div className="mx-auto mb-2 flex h-14 w-14 items-center justify-center rounded-xl bg-secondary">{icon}</div>
        <CardTitle>{title}</CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent>{children}</CardContent>
    </Card>
  </div>
);

export default InternalAccessGate;
